import { TransactionCurrency } from '@contracts/payment/Currency';
import { yupResolver } from '@hookform/resolvers/yup';
import { AvailableCurrencyDropdown } from '@src/clients/financialSettings/AvailableCurrencyDropdown';
import { CountryStateSelect } from '@src/clients/financialSettings/CountryStateSelect';
import type { CreditCardFields } from '@src/clients/financialSettings/fields/CreditCardFields';
import {
  AddressField,
  CityField,
  CountryCodeField,
  EmailField,
  FirstNameField,
  LastNameField,
  NameOnCardField,
  ZipField,
} from '@src/clients/financialSettings/fields/CreditCardFields';
import { CurrencyField } from '@src/clients/financialSettings/fields/CurrencyFields';
import { useNuveiCreditCardForm } from '@src/clients/financialSettings/hooks/useNuveiCreditCardForm';
import { NuveiForm } from '@src/clients/financialSettings/NuveiForm';
import { CreditCardInputsSchema } from '@src/clients/schemas/CreditCardInputsSchema';
import { Button } from '@src/common/components/button/Button';
import { FormTextInput } from '@src/common/components/FormTextInput';
import { LoadingOverlay } from '@src/common/components/LoadingOverlay';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { theme } from '@src/common/styles/Theme';
import { type Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props {
  clientId: string;
  onSuccess: () => void;
  onError: () => void;
  onSavedWithError: ({
    errorCode,
    description,
  }: {
    description: string;
    errorCode: string;
  }) => void;
}

export const CreditCardForm = ({
  clientId,
  onSuccess,
  onError,
  onSavedWithError,
}: Props) => {
  const { t } = useTranslation();

  const { control, formState, register, watch, getValues } = useForm({
    resolver: yupResolver(
      CreditCardInputsSchema,
    ) as unknown as Resolver<CreditCardFields>,
    mode: 'onChange',
  });
  const selectedCountryCode = watch(CountryCodeField.fieldName);
  const currency = watch(CurrencyField.fieldName);
  const {
    saveCard,
    isComplete: isNuveiFormComplete,
    isLoading,
  } = useNuveiCreditCardForm({
    clientId,
    onError,
    currency: TransactionCurrency[currency],
  });
  const gap = theme.spacing['3'];

  return (
    <Stack gap={theme.spacing['6']}>
      {isLoading && <LoadingOverlay />}
      <Stack gap={gap}>
        <Stack direction="row" gap={gap}>
          <FormTextInput
            register={register}
            errors={formState.errors}
            fieldName={NameOnCardField.fieldName}
          />
          <AvailableCurrencyDropdown control={control} />
        </Stack>
        <NuveiForm />

        <Typography>{t('billingInfo')}</Typography>

        <Stack direction="row" gap={gap}>
          <FormTextInput
            register={register}
            errors={formState.errors}
            fieldName={FirstNameField.fieldName}
          />

          <FormTextInput
            register={register}
            errors={formState.errors}
            fieldName={LastNameField.fieldName}
          />
        </Stack>

        <FormTextInput
          register={register}
          errors={formState.errors}
          fieldName={EmailField.fieldName}
        />

        <FormTextInput
          register={register}
          errors={formState.errors}
          fieldName={AddressField.fieldName}
        />

        <CountryStateSelect
          control={control}
          selectedCountryCode={selectedCountryCode}
        />

        <Stack direction="row" gap={gap}>
          <FormTextInput
            register={register}
            errors={formState.errors}
            fieldName={CityField.fieldName}
          />

          <FormTextInput
            register={register}
            errors={formState.errors}
            fieldName={ZipField.fieldName}
          />
        </Stack>
      </Stack>

      <Button
        variant="contained"
        disabled={!formState.isValid || !isNuveiFormComplete}
        onClick={() =>
          saveCard({
            creditCardFields: getValues(),
            onSuccess,
            onSavedWithError,
          })
        }
      >
        {t('save')}
      </Button>
    </Stack>
  );
};
